import { Controller } from "@hotwired/stimulus"
import { visitIndexWithOptions } from "../utils/index_options_visit"

const CUSTOM_YEAR_VALUE = "custom"

// Multi-select / date / number range / text filters in turbo index column headers, with sort.
export default class extends Controller {
  static targets = ["values"]
  static values = {
    attribute: String,
    // An enum menu may also filter another column on a date range (when that status was set).
    dateAttribute: String,
    kind: { type: String, default: "enum" },
    options: { type: Object, default: {} },
    path: String,
    searchResultsFrameId: String,
  }

  connect() {
    this.menu = this.element.querySelector(".index-header-filter__menu")
    this.boundKeepOpen = (event) => event.stopPropagation()
    // Bootstrap ferme au click/mousedown document : on bloque la remontée depuis le menu
    ;["click", "mousedown", "pointerdown"].forEach((type) => {
      this.menu?.addEventListener(type, this.boundKeepOpen)
    })

    this.syncCustomDateRange()
  }

  disconnect() {
    ;["click", "mousedown", "pointerdown"].forEach((type) => {
      this.menu?.removeEventListener(type, this.boundKeepOpen)
    })
  }

  syncCustomDateRange() {
    const datesBlock = this.element.querySelector(".index-header-filter__dates")
    if (!datesBlock) return

    const hasYearPicker = this.element.querySelector(".index-header-filter__years")
    if (!hasYearPicker) {
      datesBlock.classList.remove("d-none")
      return
    }

    const year = this.element.querySelector('[name="year"]:checked')?.value
    datesBlock.classList.toggle("d-none", year !== CUSTOM_YEAR_VALUE)
  }

  submit(event) {
    event.preventDefault()
    event.stopPropagation()

    const form = event.currentTarget
    // `submitter` est nul quand c'est `clear()` qui resoumet : il a alors noté SON bouton,
    // pour que le spinner s'allume là où le curseur est, et pas sur « Appliquer ».
    const submitter = event.submitter || this.pendingSubmitter || form.querySelector('button[type="submit"]')
    this.pendingSubmitter = null
    const options = structuredClone(this.optionsValue || {})
    const f = { ...(options.f || {}) }
    const attr = this.attributeValue

    if (this.kindValue === "enum") {
      this.applyValues(f, attr, this.selectedValues(form, "v[]"))
      if (this.dateAttributeValue) {
        if (this.listFor("d[]")) this.applyValues(f, this.dateAttributeValue, this.selectedValues(form, "d[]"))
        else this.applyDateRange(form, f, this.dateAttributeValue)
      }
    } else if (this.kindValue === "date") {
      this.applyDateRange(form, f, attr)
    } else if (this.kindValue === "range") {
      const from = form.querySelector('[name="from"]')?.value || ""
      const to = form.querySelector('[name="to"]')?.value || ""
      if (!from && !to) {
        delete f[attr]
      } else {
        f[attr] = { [this.filterId()]: { o: "between", v: ["", from, to] } }
      }
    } else if (this.kindValue === "text") {
      const query = (form.querySelector('[name="q"]')?.value || "").trim()
      if (!query) {
        delete f[attr]
      } else {
        f[attr] = { [this.filterId()]: { o: "like", v: query } }
      }
    }

    this.applySort(form, options, attr)

    if (Object.keys(f).length === 0) {
      delete options.f
    } else {
      options.f = f
    }

    delete options.page

    this.visit(options, form, submitter)
  }

  applyDateRange(form, f, attr) {
    let from = form.querySelector('[name="from"]')?.value || ""
    let to = form.querySelector('[name="to"]')?.value || ""
    const yearPicker = form.querySelector('[name="year"]')
    const year = form.querySelector('[name="year"]:checked')?.value || ""

    if (year && year !== CUSTOM_YEAR_VALUE) {
      from = `${year}-01-01`
      to = `${year}-12-31`
    } else if (yearPicker && !year) {
      // « Toutes » : les champs Du/Au sont masqués mais gardent la plage de l'année qu'on quitte
      // (ex. 2025) ; sans les ignorer, le filtre revenait à chaque fois.
      from = ""
      to = ""
    }

    if (!from && !to) {
      delete f[attr]
    } else {
      f[attr] = { [this.filterId()]: { o: "between", v: ["", from, to] } }
    }
  }

  // `null` keeps the values already filtered on (a list not loaded yet).
  applyValues(f, attr, values) {
    if (values?.length === 0) {
      delete f[attr]
    } else if (values) {
      f[attr] = { [this.filterId()]: { v: values } }
    }
  }

  // Lists loaded when the menu opens keep their checks across the searches that reload them, as in a
  // spreadsheet filter; `null` while a list is not loaded yet.
  selectedValues(form, name) {
    if (this.listFor(name)) return this.checked?.[name] ? Array.from(this.checked[name]) : null

    return Array.from(form.querySelectorAll(`input[name="${name}"]:checked`)).map((el) => el.value)
  }

  listFor(name) {
    return this.valuesTargets.find((frame) => frame.dataset.valuesName === name)
  }

  trackValue(event) {
    const input = event.target
    const set = this.checked?.[input.name]
    if (!set) return

    if (input.checked) set.add(input.value)
    else set.delete(input.value)
  }

  restoreValues(event) {
    const frame = event.target
    const name = frame.dataset.valuesName
    const inputs = frame.querySelectorAll(`input[name="${name}"]`)
    this.checked ||= {}

    if (!this.checked[name]) {
      this.checked[name] = new Set(Array.from(inputs).filter((input) => input.checked).map((input) => input.value))
      return
    }
    inputs.forEach((input) => {
      input.checked = this.checked[name].has(input.value)
    })
  }

  searchValues(event) {
    clearTimeout(this.searchTimer)
    const query = event.target.value.trim()
    const frame = this.listFor("v[]")
    if (!frame) return

    this.searchTimer = setTimeout(() => {
      const url = new URL(frame.dataset.valuesSrc, window.location.origin)
      if (query) url.searchParams.set("q", query)
      frame.src = url.pathname + url.search
    }, 250)
  }

  keepMenuOnEnter(event) {
    event.preventDefault()
  }

  // Tri proposé par le menu (radios « sort ») : n'importe quel genre de filtre peut en avoir.
  applySort(form, options, attr) {
    if (!form.querySelector('[name="sort"]')) return

    const sort = form.querySelector('[name="sort"]:checked')?.value || ""
    if (sort === "asc") {
      options.sort_by = attr
      options.sort_reverse = "false"
    } else if (sort === "desc") {
      options.sort_by = attr
      options.sort_reverse = "true"
    } else if (String(options.sort_by || "").split(".").pop() === attr) {
      delete options.sort_by
      delete options.sort_reverse
    }
  }

  clear(event) {
    event.preventDefault()
    event.stopPropagation()

    const form = this.element.querySelector("form")
    if (!form) return

    // `requestSubmit()` ne prend pour submitter qu'un bouton `type="submit"` du formulaire —
    // « Effacer » n'en est pas un. On le passe donc à `submit` par ce champ.
    this.pendingSubmitter = event.currentTarget

    form.querySelectorAll('input[name="v[]"]').forEach((el) => {
      el.checked = false
    })
    this.valuesTargets.forEach((frame) => {
      this.checked ||= {}
      this.checked[frame.dataset.valuesName] = new Set()
      frame.querySelectorAll("input[type=checkbox]").forEach((input) => { input.checked = false })
    })
    form.querySelectorAll('input[name="from"], input[name="to"], input[name="q"]').forEach((el) => {
      el.value = ""
    })
    const yearNone = form.querySelector('input[name="year"][value=""]')
    if (yearNone) yearNone.checked = true
    const none = form.querySelector('input[name="sort"][value=""]')
    if (none) none.checked = true

    this.syncCustomDateRange()
    form.requestSubmit()
  }

  filterId() {
    return String(Math.floor(Math.random() * 90000) + 10000)
  }

  // Le squelette qui s'installe dans le tableau est à l'autre bout de l'écran : le bouton
  // qu'on vient de cliquer doit accuser réception là où est le curseur. Même mécanique que le
  // submit des formulaires — `aria-busy` posé sur le formulaire ET sur le bouton, tout
  // l'habillage venant du CSS (cf. `_create_submit_transition.scss`), donc rien n'est
  // reconstruit et il n'y a rien à remettre en place.
  //
  // Sur une réponse utile, le formulaire part avec le `#…_search_results` remplacé : lever
  // l'attente ne porte alors que sur un nœud détaché, sans effet. C'est l'échec (repli sur
  // une visite pleine page, erreur réseau) qui a besoin du `finally`, sinon le menu restait
  // inerte, spinner tournant, sous l'ancien tableau.
  async visit(options, form = null, submitter = null) {
    this.setPending(form, submitter, true)

    try {
      await visitIndexWithOptions({
        path: this.pathValue,
        options,
        element: this.element,
        searchResultsFrameId: this.searchResultsFrameIdValue,
      })
    } finally {
      this.setPending(form, submitter, false)
    }
  }

  setPending(form, submitter, pending) {
    ;[form, submitter].forEach((element) => {
      if (!element) return
      if (pending) {
        element.setAttribute("aria-busy", "true")
      } else {
        element.removeAttribute("aria-busy")
      }
    })

    // `disabled` en plus de l'habillage : c'est lui qui empêche réellement un second envoi
    // pendant que le premier est en vol. Posé APRÈS la soumission, il n'empêche pas celle-ci.
    if (!submitter) return
    submitter.disabled = pending
  }
}
